import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { ChevronLeft, Settings, Eye, EyeOff, Calendar, Users, User } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

const STORAGE_KEY = "rotalie_staff_preferences";

// Default preferences
const DEFAULT_PREFERENCES = {
  defaultView: "day", // 'day' or 'week'
  defaultLayout: "cards", // 'cards' (All Staff) or 'myshifts'
  showNotWorking: false, // hide by default
};

const StaffSettings = () => {
  const navigate = useNavigate();
  
  // Load preferences from localStorage using lazy initialization
  const [preferences, setPreferences] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...DEFAULT_PREFERENCES, ...parsed };
      }
    } catch (e) {
      console.error("Failed to parse preferences:", e);
    }
    return DEFAULT_PREFERENCES;
  });
  const [hasChanges, setHasChanges] = useState(false);

  const updatePreference = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const savePreferences = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    setHasChanges(false);
    toast.success("Preferences saved");
  };

  const resetToDefaults = () => {
    setPreferences(DEFAULT_PREFERENCES);
    localStorage.removeItem(STORAGE_KEY);
    setHasChanges(false);
    toast.success("Preferences reset to defaults");
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/staff")} data-testid="back-btn">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl font-bold">Settings</h1>
          <p className="text-sm text-muted-foreground">Configure your preferences</p>
        </div>
      </div>

      {/* Default View */}
      <Card>
        <CardHeader className="py-3 px-4 bg-slate-50 dark:bg-slate-900/30 border-b">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Default View
          </CardTitle>
          <CardDescription className="text-xs">Choose which view to show when you open the app</CardDescription>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          <div className="flex gap-2">
            <button
              onClick={() => updatePreference("defaultView", "day")}
              className={cn(
                "flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition-all",
                preferences.defaultView === "day"
                  ? "bg-violet-600 text-white border-violet-600"
                  : "bg-background hover:bg-muted"
              )}
              data-testid="pref-view-day"
            >
              Day
            </button>
            <button
              onClick={() => updatePreference("defaultView", "week")}
              className={cn(
                "flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition-all",
                preferences.defaultView === "week"
                  ? "bg-violet-600 text-white border-violet-600"
                  : "bg-background hover:bg-muted"
              )}
              data-testid="pref-view-week"
            >
              Week
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Default Layout */}
      <Card>
        <CardHeader className="py-3 px-4 bg-slate-50 dark:bg-slate-900/30 border-b">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Users className="w-4 h-4" />
            Default Layout
          </CardTitle>
          <CardDescription className="text-xs">Choose which staff view to show by default</CardDescription>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          <div className="flex gap-2">
            <button
              onClick={() => updatePreference("defaultLayout", "cards")}
              className={cn(
                "flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition-all flex items-center justify-center gap-2",
                preferences.defaultLayout === "cards"
                  ? "bg-violet-600 text-white border-violet-600"
                  : "bg-background hover:bg-muted"
              )}
              data-testid="pref-layout-allstaff"
            >
              <Users className="w-4 h-4" />
              All Staff
            </button>
            <button
              onClick={() => updatePreference("defaultLayout", "myshifts")}
              className={cn(
                "flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition-all flex items-center justify-center gap-2",
                preferences.defaultLayout === "myshifts"
                  ? "bg-violet-600 text-white border-violet-600"
                  : "bg-background hover:bg-muted"
              )}
              data-testid="pref-layout-myshifts"
            >
              <User className="w-4 h-4" />
              My Shifts
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Display Options */}
      <Card>
        <CardHeader className="py-3 px-4 bg-slate-50 dark:bg-slate-900/30 border-b">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Display Options
          </CardTitle>
          <CardDescription className="text-xs">Customize what information is shown</CardDescription>
        </CardHeader>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {preferences.showNotWorking ? (
                <Eye className="w-5 h-5 text-muted-foreground" />
              ) : (
                <EyeOff className="w-5 h-5 text-muted-foreground" />
              )}
              <div>
                <Label htmlFor="show-not-working" className="text-sm font-medium">
                  Show employees not working
                </Label>
                <p className="text-xs text-muted-foreground">
                  Display staff members who are off today
                </p>
              </div>
            </div>
            <Switch
              id="show-not-working"
              checked={preferences.showNotWorking}
              onCheckedChange={(checked) => updatePreference("showNotWorking", checked)}
              data-testid="pref-show-not-working"
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          className="flex-1"
          onClick={resetToDefaults}
          data-testid="reset-preferences-btn"
        >
          Reset to Defaults
        </Button>
        <Button
          className="flex-1 bg-violet-600 hover:bg-violet-700"
          onClick={savePreferences}
          disabled={!hasChanges}
          data-testid="save-preferences-btn"
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default StaffSettings;
